import { z } from 'zod';
import { assignedAgencySchema } from '../features/auth/agencyAccess';

import type { ShigoSession } from '../features/auth/model';
import { NetworkAvailableFeature } from '../features/auth/networkFeatures';
import { resolveLegacyRolePermissions, ShigoPermissionSource } from '../features/auth/permissions';
import { runtimeNetworkBrandDetailsSchema } from './networkBranding';

export const SHIGO_SESSION_STORAGE_KEY = 'shigo.session';

const persistedSessionSchema = z.object({
  accessToken: z.string().trim().min(1),
  user: z.object({
    id: z.string().optional(),
    email: z.string().optional(),
    name: z.string().optional(),
    username: z.string().optional(),
    publisherIds: z.array(z.string()).default([]),
    agencies: z.array(assignedAgencySchema).optional(),
    roles: z.array(z.string()).default([]),
    permissions: z.array(z.string()).optional(),
    networkFeatures: z.array(z.enum(NetworkAvailableFeature)).default([]),
    useStitcher: z.boolean().optional(),
    networkBrandDetails: runtimeNetworkBrandDetailsSchema.optional(),
    permissionSource: z.enum(ShigoPermissionSource).optional(),
  }),
});

export function loadShigoSession(
  storage: Storage,
  key = SHIGO_SESSION_STORAGE_KEY,
): ShigoSession | null {
  const rawSession = storage.getItem(key);
  if (!rawSession) {
    return null;
  }

  try {
    const result = persistedSessionSchema.safeParse(JSON.parse(rawSession));
    return result.success
      ? {
          accessToken: result.data.accessToken,
          user: persistedShigoUser(result.data.user),
        }
      : null;
  } catch {
    return null;
  }
}

function persistedShigoUser(session: z.infer<typeof persistedSessionSchema>['user']) {
  return {
    ...(session.id ? { id: session.id } : {}),
    ...(session.email ? { email: session.email } : {}),
    ...(session.name ? { name: session.name } : {}),
    ...(session.username ? { username: session.username } : {}),
    publisherIds: session.publisherIds,
    ...(session.agencies ? { agencies: session.agencies } : {}),
    roles: session.roles,
    permissions: resolvePersistedPermissions(session),
    networkFeatures: session.networkFeatures,
    ...(session.useStitcher === undefined ? {} : { useStitcher: session.useStitcher }),
    ...(session.networkBrandDetails ? { networkBrandDetails: session.networkBrandDetails } : {}),
    ...(session.permissionSource ? { permissionSource: session.permissionSource } : {}),
  };
}

function resolvePersistedPermissions(session: z.infer<typeof persistedSessionSchema>['user']) {
  if (session.permissionSource === ShigoPermissionSource.Backend) {
    return session.permissions ?? [];
  }

  return resolveLegacyRolePermissions(session.roles);
}

export function saveShigoSession(
  storage: Storage,
  session: ShigoSession,
  key = SHIGO_SESSION_STORAGE_KEY,
) {
  storage.setItem(key, JSON.stringify(session));
}

export function clearShigoSession(storage: Storage, key = SHIGO_SESSION_STORAGE_KEY) {
  storage.removeItem(key);
}

export function getBrowserStorage(): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const storage: unknown = window.localStorage;
  if (
    typeof storage === 'object' &&
    storage !== null &&
    'getItem' in storage &&
    'setItem' in storage &&
    'removeItem' in storage
  ) {
    return storage as Storage;
  }

  return null;
}
